import { todayDate } from '../../../common/src/date_string.ts';
import { ClientOrg, Hymn, Hymnal, HymnalId, HymnalSlug, SongList, SongListType } from '../../../common/src/model.ts';
import { HymnalIcon } from './use_hymnals.tsx';

export interface HymnalWithHymns extends Hymnal {
  hymns: Hymn[];
  message?: any; // JSX.element (bogus) -> this message property should be eliminated or otherwise refactored
  showRedDot?: boolean; // bogus - the red dot in the event heading should be rendered by the Event page
  icon?: HymnalIcon;
  hideTitle?: boolean;
  isPreview?: boolean; // display a "preview" badge next to the title (onboarding demo page)
  songList?: SongList;
}

// NOTE: use getSongListName to get the name of the song list
export function getSongListFromHymnal(hymnal: Pick<HymnalWithHymns, 'hymns' | 'name' | 'songList'>): SongList {
  if (hymnal.songList) {
    return hymnal.songList;
  }
  return getSongListFromHymns(hymnal.name, hymnal.hymns);
}

export function getSongListFromHymns(name: string, hymns: Array<Pick<Hymn, 'slug'>>): SongList {
  return {
    type: SongListType.Playlist,
    name: name,
    date: todayDate(),
    songs: [...hymns.entries()].map(([index, { slug }]) => ({ row: index + 1, slug })),
  };
}

/*************************** alternate titles (issue #3454) ***************************/

// The hymnal whose songs a Library entry shows: itself, or for an alternate title the canonical
// hymnal it aliases
export function getCanonicalHymnal<T extends Hymnal>(hymnals: T[], hymnal: T): T {
  if (!hymnal.aliasOf) {
    return hymnal;
  }
  return hymnals.find(({ slug }) => slug === hymnal.aliasOf) ?? hymnal;
}

// The slug that appears in song slugs for this hymnal
export function getCanonicalHymnalSlug(hymnal: Pick<Hymnal, 'slug' | 'aliasOf'>): HymnalSlug {
  return hymnal.aliasOf ?? hymnal.slug;
}

// Every title of the book a hymnal belongs to: the canonical hymnal plus its alternate titles
export function getTitlesOfHymnal<T extends Hymnal>(hymnals: T[], hymnal: T): T[] {
  const canonical = getCanonicalHymnal(hymnals, hymnal);
  return hymnals.filter((candidate) => candidate.id === canonical.id || candidate.aliasOf === canonical.slug);
}

// ids of every hymnal an org selected, whether as one of its hymnals or as a primary hymnal
export function getOrgHymnalIds(org: Pick<ClientOrg, 'hymnals' | 'primaryHymnalIds'> | undefined): Set<HymnalId> {
  return new Set<HymnalId>([...(org?.hymnals?.map(({ id }) => id) ?? []), ...(org?.primaryHymnalIds ?? [])]);
}

// The title to show for a hymnal: the one the org selected, else the first title hint that names
// a title of the same book (e.g. the playlist the song plays from, the hymnal being browsed),
// else the canonical title
export function getDisplayHymnal<T extends Hymnal>({
  hymnals,
  hymnal,
  org,
  titleHints,
}: {
  hymnals: T[];
  hymnal: T;
  org: Pick<ClientOrg, 'hymnals' | 'primaryHymnalIds'> | undefined;
  titleHints: Array<string | undefined>;
}): T {
  const canonical = getCanonicalHymnal(hymnals, hymnal);
  const titles = getTitlesOfHymnal(hymnals, canonical);
  if (titles.length <= 1) {
    return canonical;
  }
  const orgHymnalIds = getOrgHymnalIds(org);
  const orgSelectedTitle = titles.find(({ id }) => orgHymnalIds.has(id));
  if (orgSelectedTitle) {
    return orgSelectedTitle;
  }
  for (const hint of titleHints) {
    const hintedTitle = hint === undefined ? undefined : titles.find(({ name }) => name === hint);
    if (hintedTitle) {
      return hintedTitle;
    }
  }
  return canonical;
}

// Whether a title belongs in the song selector, which shows one entry per book: an alternate
// title only when the org selected it, and the canonical title unless the org selected one of
// its alternate titles instead
export function isSongSelectorTitle<T extends Hymnal>(
  hymnals: T[],
  hymnal: T,
  org: Pick<ClientOrg, 'hymnals' | 'primaryHymnalIds'> | undefined,
): boolean {
  const orgHymnalIds = getOrgHymnalIds(org);
  if (hymnal.aliasOf) {
    return orgHymnalIds.has(hymnal.id);
  }
  if (orgHymnalIds.has(hymnal.id)) {
    return true;
  }
  return !hymnals.some((candidate) => candidate.aliasOf === hymnal.slug && orgHymnalIds.has(candidate.id));
}
